/* eslint-disable padding-line-between-statements */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import _ from 'lodash';
import { BreakpointTrackerContext } from '@jutro/layout';
import { TranslatorContext } from '@jutro/locale';
import { useModal } from '@jutro/components';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { withRouter } from 'react-router-dom';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useAuthentication, withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import wizardStyles from 'twia-portals-wizard-components-ui/common/TWIAWizard.module.scss'
import TWIADateFormatUtil from "ent-capability-quoteandbind-common-react/util/TWIADateFormatUtil";
// import PrintDocumentUtil from 'ent-capability-quoteandbind-common-react/util/PrintDocumentUtil.js';
import { Link } from '@jutro/router';
import metadata from './SuccessPage.metadata.json5';
import messages from './SuccessPage.messages.js';
import styles from './SuccessPage.module.scss';


function SuccessPage(props) {
    const breakpoint = useContext(BreakpointTrackerContext);
    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const [initialSubmission, setInitialSubmission] = useState(null);
    const { location } = props;
    const submissionVM = location.state.response;
    const autHeader = location.state.authHeader;
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { showAlert, showConfirm } = useModal();
    const [isLoading, setIsLoading] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const quoteID = _.get(submissionVM, 'quoteID');
    const policyNumber = _.get(submissionVM, 'bindData.policyNumber');
    const documentPath = `${process.env.PUBLIC_PATH}/quotes/${quoteID}/documents`
    const internalPath = `${process.env.PUBLIC_PATH}/quotes/${quoteID}/summary`
    const redirectPath = `${process.env.PUBLIC_PATH}/policies/${policyNumber}/summary`
    const accountType = _.get(submissionVM, 'baseData.accountHolder.subtype');
    const isIssued = _.get(submissionVM, 'baseData.periodStatus') === 'Bound';
    const isInsuredPay = _.get(submissionVM, 'bindData.paymentDetails.nonEFTPaymentMethod_TWIA') === 'ins_direct_pay';
    const eftEnabled = _.get(submissionVM, 'bindData.eftEnabled_TWIA');
    const chosenPlan = _.find(submissionVM.bindData.paymentPlans, {
        billingId: submissionVM.bindData?.selectedPaymentPlan
    });
    const insuredDetails = _.find(submissionVM.baseData.policyContactDetails_TWIA, { policyContactRoles: [{ subtype: 'PolicyPriNamedInsured' }] });

    const getOfferOrPolicyNumber = useCallback(() => {
        if (isIssued) {
            return (
                <Link to={redirectPath}>
                    {policyNumber}
                </Link>
            )
        };
        return (
            <Link to={internalPath}>
                {quoteID}
            </Link>
        );
    }, [internalPath, isIssued, policyNumber, quoteID, redirectPath]);
    
    const quotePrintDocument = useCallback(async () => {
        window.print() // DE16769
    }, []);
    
    const contactNumber = useCallback(() => {
        const contact = formatPhoneNumber(insuredDetails);
        if (contact) {
            return contact;
        }
        return null;
    }, []);


    const formatPhoneNumber = (contact) => {
        const number = getPrimaryPhone(contact);
        if (number === undefined || number === null) {
            return null;
        }
        return number;
    };

    const getPrimaryPhone = (contact) => {
        switch (contact.primaryPhoneType) {
            case 'home':
                return contact.homeNumber;
            case 'work':
                return contact.workNumber;
            case 'mobile':
                return contact.cellNumber;
            default:
                return null;
        }
    };

    // DE16752
    const paymentMethod = viewModelService?.productMetadata?.get('pc').types.getTypelist('PaymentMethod_TWIA');

    const getSelectedPaymentMethod = useCallback(() => (!eftEnabled
        // eslint-disable-next-line no-secrets/no-secrets
        ? _.get(submissionVM, `bindData.paymentDetails.nonEFTPaymentMethod_TWIA`)
        // eslint-disable-next-line no-secrets/no-secrets
        : _.get(submissionVM, `bindData.paymentDetails.paymentMethod_TWIA`)),
        [eftEnabled, submissionVM]);
        
    const selectPayment = useCallback(() => {
        const method = getSelectedPaymentMethod();

        if (paymentMethod !== undefined) {
            const result = paymentMethod?.codes?.filter(item => {
                if (item.code === method) {
                    return item;
                }
            })
            return translator({
                id: result[0].name,
                defaultMessage: result[0].name
            })
        }
    }, [paymentMethod, submissionVM, translator]);

    const periodEndDate = _.get(submissionVM, `baseData.periodEndDate`);
    const periodStartDate = _.get(submissionVM, `baseData.periodStartDate`);

    const formatPolicyPeriod = (startDate, endDate) => {
        const formattedStartDate = TWIADateFormatUtil.formatDate(startDate);
        const formattedEndDate = TWIADateFormatUtil.formatDate(endDate);

        return `${formattedStartDate} - ${formattedEndDate}`;
    };

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
            readOnly: true
        },
        policyText: {
            content: isIssued ? translator(messages.issuedMessage) : translator(messages.offerSubmittedMessage)
        },
        policyNumber: {
            label: isIssued ? translator(messages.policyNumber) : translator(messages.offerNumber),
            value: getOfferOrPolicyNumber()
        },
        documentReview: {
            content: isIssued ? translator(messages.issuedApplicationContent) : translator(messages.applicationContent)
        },
        policyPeriod: {
            label: isIssued ? 'Policy Period' : 'Proposed Policy Period',
            value: formatPolicyPeriod(periodStartDate, periodEndDate) // DE16752
        },
        policyEffectiveDate: {
            label: isIssued ? translator(messages.policyEffectiveDate) : translator(messages.requestedStartDate)
        },
        firstName : {
            visible: accountType === 'Person',
            value: insuredDetails.firstName
        },
        lastName : {
            visible: accountType === 'Person',
        },
        companyName : {
            visible: accountType === 'Company',
        },
        confirmationNumber: {
            visible: isIssued && _.get(submissionVM, 'bindData.paymentDetails.confirmationNumber_TWIA') !== undefined
        },
        downPaymentConfirmationNumber: {
            visible: isIssued && isInsuredPay
        },
        paymentPlan: {
            value: chosenPlan?.name
        },
        totalPremium: {
            value: {
                amount: _.get(submissionVM, 'quoteData.offeredQuotes[0].premium.total.amount'),
                currency: "USD"
            }
        },
        fileExtensionLink: {
            to: documentPath
        },
        paymentMethodWithEft: {
            visible: eftEnabled
        },
        paymentMethod: {
            visible: !eftEnabled,
            value : selectPayment()
        },
        phoneNumber: {
            value: contactNumber()
        },
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            quotePrintDocument
        },
    }

    return (
        <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                // onModelChange={updateWizardData}
                overrideProps={overrideProps}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
        />
    );
}

SuccessPage.propTypes = wizardProps;
export default withRouter(SuccessPage);
